import React, { useEffect, useMemo } from 'react';
import { getUpdatedDisplayLink } from 'util/url';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import ExternalResourceIcon from '@helpCenter/topics/src/common/ui/external-resource-fallback-icon';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { Link } from 'view/common/analytics';
import messages from 'view/help-center-topics/topic/messages';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import * as bp from 'view/styles/breakpoints';
import * as fonts from 'view/styles/fonts';
import { AnalyticsContext, withAnalyticsEvents } from '@atlaskit/analytics-next';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { AutoScaledImage } from '@atlassian/help-center-common-component/auto-scaled-image';
import { ThemedLink } from '@atlassian/help-center-common-component/themed-link';
import { isExternalResource, urlRefsAndTarget } from '@atlassian/help-center-common-util/is-external-resource';
import type { topicCardFragment$key } from './__generated__/topicCardFragment.graphql';
import type { iconTypes } from 'topics/src/common/ui/external-resource-fallback-icon/types';

interface Props {
    topic: topicCardFragment$key;
}

export const Topic = (props: Props & WithAnalyticsEventsProps) => {
    di(useIsPageInEditMode);
    const { topicName, items, topicId } = useFragment<topicCardFragment$key>(
        graphql`
            fragment topicCardFragment on HelpLayoutTopic {
                topicName
                topicId
                items {
                    __id
                    title
                    logo
                    displayLink
                    entityKey
                    helpObjectType
                }
            }
        `,
        props.topic
    );

    const intl = useIntl();
    const [isPageInEditMode] = useIsPageInEditMode();
    const MAX_HELP_OBJECTS = 3;

    const allHelpObjects = useMemo(() => items || [], [items]);

    useEffect(() => {
        !isPageInEditMode &&
            allHelpObjects.forEach((o) => {
                if (!o?.title) {
                    const { createAnalyticsEvent } = props;

                    if (createAnalyticsEvent) {
                        const analyticsEvent = createAnalyticsEvent({
                            analyticsType: OPERATIONAL_EVENT_TYPE,
                            action: 'invalidHelpObjectFound',
                            actionSubject: 'helpObject',
                        });
                        analyticsEvent.fire();
                    }
                }
            });
    }, [isPageInEditMode, allHelpObjects, props]);

    const showMoreCount = allHelpObjects.length - MAX_HELP_OBJECTS;
    const TopicName = isPageInEditMode ? TopicNameWithoutLink : TopicNameLink;
    const ShowMore = isPageInEditMode ? ShowMoreWithoutLink : ShowMoreLink;

    const getResourceType = (helpObjectType: string): iconTypes => {
        switch (helpObjectType) {
            case 'HelpObjectStoreArticle':
                return 'ARTICLE';
            case 'HelpObjectStoreRequestForm':
                return 'REQUEST_FORM';
            case 'HelpObjectStoreChannel':
                return 'CHANNEL';
            default:
                return 'REQUEST_FORM';
        }
    };

    if (allHelpObjects.length) {
        return (
            <Container>
                <TopicName to={topicId ? `/topic/${topicId}` : ''} actionSubjectId={'topicTitle'}>
                    {isPageInEditMode ? (
                        <TextContent>{topicName}</TextContent>
                    ) : (
                        <Tooltip content={topicName}>
                            <TextContent>{topicName}</TextContent>
                        </Tooltip>
                    )}
                </TopicName>
                <HelpObjects>
                    {allHelpObjects.slice(0, MAX_HELP_OBJECTS).map((helpObject, index) => (
                        <ListItem key={helpObject?.__id}>
                            <AnalyticsContext data={{ attributes: { helpObjectPosition: index + 1 } }}>
                                {helpObject && (
                                    <StyledHelpObject>
                                        <ImageContainer>
                                            {helpObject.logo && (
                                                <AutoScaledImage
                                                    src={helpObject.logo}
                                                    alt={helpObject.title ?? ''}
                                                    inheritWidth
                                                />
                                            )}
                                            {!helpObject.logo && helpObject?.helpObjectType ? (
                                                <ExternalResourceIcon
                                                    iconUrl={getUpdatedDisplayLink(helpObject?.displayLink || '')}
                                                    resourceType={getResourceType(helpObject?.helpObjectType)}
                                                    size={'medium'}
                                                />
                                            ) : null}
                                        </ImageContainer>
                                        {isPageInEditMode ? (
                                            <HelpObjectTitle>{helpObject.title}</HelpObjectTitle>
                                        ) : (
                                            <TopicLink
                                                {...urlRefsAndTarget({
                                                    isAnExternalResource: isExternalResource(
                                                        helpObject?.entityKey as string
                                                    ),
                                                    displayLink: getUpdatedDisplayLink(helpObject?.displayLink || ''),
                                                })}
                                                actionSubjectId="helpObject"
                                            >
                                                <Tooltip content={helpObject.title}>
                                                    <HelpObjectTitle>{helpObject.title}</HelpObjectTitle>
                                                </Tooltip>
                                            </TopicLink>
                                        )}
                                    </StyledHelpObject>
                                )}
                            </AnalyticsContext>
                        </ListItem>
                    ))}
                </HelpObjects>
                {showMoreCount > 0 && (
                    <ShowMore to={topicId ? `/topic/${topicId}` : ''} actionSubjectId={'showMoreHelpObjects'}>
                        <TextContent>
                            {intl.formatMessage(messages.showMoreText, {
                                count: showMoreCount,
                            })}
                        </TextContent>
                    </ShowMore>
                )}
            </Container>
        );
    }
    return null;
};

export default withAnalyticsEvents()(Topic);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const TopicNameLink = styled(ThemedLink)`
    ${fonts.h400};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    ${bp.fromXSmall.css`
        ${fonts.h500};
    `};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const TopicNameWithoutLink = styled.div`
    ${fonts.h400};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    color: ${(props) => token('color.link', props.theme.color)};
    ${bp.fromXSmall.css`
        ${fonts.h500};
    `};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const HelpObjects = styled.ol`
    ${fonts.xsmall};
    padding: ${token('space.0', '0px')};
    list-style-type: none;
    display: flex;
    flex-direction: column;
    width: 100%;
    ${bp.fromXSmall.css`
        ${fonts.regular};
    `};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ListItem = styled.li`
    margin-top: ${token('space.0', '0px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    height: 100%;
    & ${HelpObjects} {
        margin: ${token('space.200', '16px')} ${token('space.0', '0px')} ${token('space.0', '0px')};
    }
    & ${TopicNameLink} {
        margin-top: ${token('space.0', '0px')};
    }
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const StyledHelpObject = styled.div`
    line-height: ${token('space.300', '24px')};
    display: flex;
    margin-bottom: ${token('space.150', '12px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ImageContainer = styled.div`
    height: ${token('space.300', '24px')};
    width: ${token('space.300', '24px')};
    margin-right: ${token('space.100', '8px')};
    flex-shrink: 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const HelpObjectTitle = styled.div`
    white-space: nowrap;
    width: 100%;
    color: ${token('color.text', colors.N800)};
    text-overflow: ellipsis;
    overflow: hidden;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ShowMoreLink = styled(ThemedLink)`
    margin-top: auto;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const TopicLink = styled(Link)`
    overflow: hidden;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const TextContent = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ShowMoreWithoutLink = styled.div`
    color: ${(props) => token('color.link', props.theme.color)};
`;
